import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ArticleDTO } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Markdown } from '@/components/ui/Markdown';
import { Button } from '@/components/ui/Button';
import { BookMarked, Bot } from 'lucide-react';
import { useMe } from '@/hooks/useMe';
import { haptic } from '@/lib/tg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();

  const { data: article, isLoading } = useQuery<ArticleDTO>({
    queryKey: ['article', id],
    queryFn: () => api.get<ArticleDTO>(`/api/training/articles/${id}`),
    enabled: !!id,
  });

  const toggleBookmark = useMutation({
    mutationFn: async () => {
      if (!article) return;
      if (article.isBookmarked) {
        await api.delete<{ ok: true }>(`/api/training/bookmarks/${article.id}`);
      } else {
        await api.post<{ ok: true }>(`/api/training/bookmarks/${article.id}`, {});
      }
    },
    onSuccess: () => {
      haptic('light');
      qc.invalidateQueries({ queryKey: ['article', id] });
      qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });

  if (isLoading || !article) return <div className="p-6 text-tg-hint">Загрузка…</div>;

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title={article.title} />

      <div className="p-4 space-y-4">
        <Markdown>{article.bodyMd}</Markdown>

        <div className="text-xs text-tg-hint text-center italic pt-4 border-t border-tg-secondaryBg">
          Amrita Knowledge Base · @{me?.tgUsername ?? 'user'} ·{' '}
          {new Date().toLocaleDateString('ru-RU')}
        </div>

        <div className="flex gap-2 sticky bottom-4">
          <Button
            variant="primary"
            className="flex-1"
            onClick={() => nav(`/ai?ctx=${encodeURIComponent(article.title)}`)}
          >
            <Bot className="w-4 h-4 mr-2" />
            Спросить ИИ об этом
          </Button>
          <Button
            variant={article.isBookmarked ? 'primary' : 'secondary'}
            onClick={() => toggleBookmark.mutate()}
            disabled={toggleBookmark.isPending}
            aria-label={article.isBookmarked ? 'Убрать из закладок' : 'Добавить в закладки'}
          >
            <BookMarked
              className={'w-4 h-4 ' + (article.isBookmarked ? 'fill-current' : '')}
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
